// Audit — 真實資料版（Route 6, PR #96）。
//
// /v1/audit-logs cross-quarter fan-out from nexus_audit_*.audit_logs.
// super_admin 可不選 tenant 看跨租戶。

import React, { useCallback, useEffect, useState } from "react";
import { Search, ShieldCheck, Bot, User, RefreshCw, ServerCrash, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import Skeleton from "../components/Skeleton.js";
import PaginationControl from "../components/PaginationControl.js";
import { listAudit, friendlyAuditError, type AuditEntry } from "../lib/auditApi.js";
import { actionLabel } from "../lib/auditActions.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { usePagination } from "../lib/usePagination.js";
import { formatTaipeiDateTime } from "../../lib/time.js";

type ListState =
  | { status: "loading" }
  | { status: "ready"; items: AuditEntry[]; total: number }
  | { status: "error"; message: string };

function formatTime(iso: string): string {
  return formatTaipeiDateTime(iso);
}

function actorLabel(e: AuditEntry): string {
  if (e.actor_kind === "system") return "系統";
  // Prefer the display_name the backend resolved from nexus_control.users;
  // when missing (deleted user, system call, etc) fall back to the first
  // 8 chars of the ULID — full ULID is too noisy for the timeline.
  if (e.actor_display_name) return e.actor_display_name;
  if (e.actor_id) return `${e.actor_id.slice(0, 8)}…`;
  return "—";
}

function tenantLabel(e: AuditEntry): string | null {
  if (!e.tenant_id) return null;
  if (e.tenant_id.startsWith("_")) return e.tenant_id; // _platform / _line_*
  if (e.tenant_name) return e.tenant_name;
  return `${e.tenant_id.slice(0, 8)}…`;
}

export default function Audit(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [q, setQ] = useState("");
  const [pendingQ, setPendingQ] = useState("");
  const pag = usePagination({ urlPrefix: "audit_" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listAudit({
        tenantId: tenantParam,
        q: pendingQ || null,
        limit: pag.limit,
        offset: pag.offset,
      });
      setState({ status: "ready", items: r.items, total: r.total });
    } catch (err) {
      setState({ status: "error", message: friendlyAuditError(err) });
    }
  }, [tenantParam, pendingQ, pag.limit, pag.offset]);

  useEffect(() => { void load(); }, [load]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Filter changed → jump to page 1 so the new query starts at the top.
    pag.withFilter(setPendingQ)(q);
  };

  return (
    <>
      <PageHeader
        title="操作紀錄"
        description={
          session.super_admin && !tenantParam
            ? "您是平台管理員。下方顯示「全平台」的操作紀錄。"
            : "設定變更、事件處理、敏感操作與 tag 綁定紀錄"
        }
        actions={
          <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
            <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
          </button>
        }
      />
      <form onSubmit={onSubmit} className="card p-3 mb-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜尋使用者、操作或對象…按 Enter 套用"
            className="input pl-9 h-9 text-sm"
          />
        </div>
      </form>
      {state.status === "loading" && (
        <div className="card p-5 space-y-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-3"><Skeleton className="h-3 w-32" /><Skeleton className="flex-1 h-3" /></div>
          ))}
        </div>
      )}
      {state.status === "error" && (
        <EmptyState icon={ServerCrash} title="稽核紀錄載入失敗" description={state.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />
      )}
      {state.status === "ready" && state.items.length === 0 && (
        <EmptyState icon={ShieldCheck} title="近 7 天沒有符合條件的紀錄" description="當有任何設定變更或敏感操作時，這裡會即時顯示。" />
      )}
      {state.status === "ready" && state.items.length > 0 && (
        <div className="card overflow-hidden">
          <ol className="relative pl-5 p-4 sm:p-5">
            <span className="absolute left-2 top-1 bottom-1 w-px bg-border" aria-hidden="true" />
            {groupByPlan(state.items).map((g) =>
              g.kind === "single" ? (
                <AuditRow key={g.entry.id} entry={g.entry} showTenant={session.super_admin === true} />
              ) : (
                <PlanGroup key={g.planId} planId={g.planId} entries={g.entries} showTenant={session.super_admin === true} />
              ),
            )}
          </ol>
          <PaginationControl
            page={pag.page}
            pageSize={pag.limit}
            total={state.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setLimit}
          />
        </div>
      )}
    </>
  );
}

function tryPretty(s: string): string {
  try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; }
}

// ── Grouping ───────────────────────────────────────────────────────────────
//
// AI 透過 propose_plan 一次推 N 個操作；PlanModal 套用時逐一打 write
// endpoint，每個 request 都帶同一個 `x-plan-correlation-id`，所有 N 筆
// audit row 共享同一個 plan_correlation_id。/dashboard/audit 把這些列
// 收合在一個「AI 計畫」banner 下，避免操作員捲動數十筆破碎稽核列。
//
// API 回傳已經是 occurred_at DESC 排序；分組過程保留時間順序：第一次遇到
// 某 plan_correlation_id → 在那個位置開一個 group，後續同 id 的列附入該
// group 即可（即使中間夾了非 plan row）。手動操作的列 (plan_correlation_id
// === null) 走 single 路徑單獨渲染。

type AuditGroup =
  | { kind: "single"; entry: AuditEntry }
  | { kind: "plan"; planId: string; entries: AuditEntry[] };

function groupByPlan(items: AuditEntry[]): AuditGroup[] {
  const out: AuditGroup[] = [];
  const planIdx = new Map<string, number>();
  for (const e of items) {
    if (!e.plan_correlation_id) {
      out.push({ kind: "single", entry: e });
      continue;
    }
    const existing = planIdx.get(e.plan_correlation_id);
    if (existing !== undefined) {
      (out[existing] as { kind: "plan"; planId: string; entries: AuditEntry[] }).entries.push(e);
    } else {
      const idx = out.push({ kind: "plan", planId: e.plan_correlation_id, entries: [e] }) - 1;
      planIdx.set(e.plan_correlation_id, idx);
    }
  }
  return out;
}

function AuditRow({ entry: a, showTenant }: { entry: AuditEntry; showTenant: boolean }): React.ReactElement {
  const isSystem = a.actor_kind === "system";
  const Icon = isSystem ? Bot : User;
  return (
    <li className="relative pb-5 last:pb-0">
      <span className="absolute -left-[18px] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-bg-base ring-2 ring-bg-surface">
        <Icon className={`h-3 w-3 ${isSystem ? "text-text-secondary" : "text-accent"}`} />
      </span>
      <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
        <span className="font-mono text-2xs text-text-muted whitespace-nowrap">{formatTime(a.occurred_at)}</span>
        <span className="text-sm text-text-primary">
          <strong className="font-medium">{actorLabel(a)}</strong>
          <span className="ml-2 text-xs text-accent">{actionLabel(a.action)}</span>
          {a.target_kind && a.target_id && (
            <span className="ml-2 font-mono text-2xs text-text-muted" title={`${a.target_kind}:${a.target_id}`}>
              ({a.target_id.slice(0, 8)}…)
            </span>
          )}
        </span>
        {tenantLabel(a) && showTenant && (
          <span className="text-2xs text-text-muted sm:ml-auto">客戶單位：{tenantLabel(a)}</span>
        )}
      </div>
      {a.diff && (
        <details className="mt-1 ml-1">
          <summary className="cursor-pointer text-2xs text-text-muted">差異內容</summary>
          <pre className="mt-1 max-h-48 overflow-auto rounded border border-border bg-bg-raised p-2 text-2xs text-text-secondary whitespace-pre-wrap">{tryPretty(a.diff)}</pre>
        </details>
      )}
    </li>
  );
}

function PlanGroup({
  planId,
  entries,
  showTenant,
}: {
  planId: string;
  entries: AuditEntry[];
  showTenant: boolean;
}): React.ReactElement {
  // Default collapsed — a 20-op plan with all rows expanded buries the
  // surrounding manual events. Operator clicks to expand when they want
  // to inspect.
  const [open, setOpen] = useState(false);
  // The group's anchor timestamp = the oldest entry (= API sort is
  // occurred_at DESC, so the LAST entry in the array is oldest). We show
  // the latest occurred_at on the header so it sorts in line with sibling
  // single rows.
  const latest = entries[0];
  const oldest = entries[entries.length - 1];
  const actor = latest;
  // Unique action label set — operators usually want to know「這個 plan
  // 做了哪幾類事」not「20 個 site.create」.
  const actionCounts = new Map<string, number>();
  for (const e of entries) actionCounts.set(e.action, (actionCounts.get(e.action) ?? 0) + 1);
  const actionSummary = [...actionCounts.entries()]
    .map(([action, n]) => `${actionLabel(action)}${n > 1 ? `×${n}` : ""}`)
    .join("、");
  const sameDay = formatTime(latest.occurred_at).slice(0, 10) === formatTime(oldest.occurred_at).slice(0, 10);
  const timeRange = sameDay
    ? formatTime(latest.occurred_at)
    : `${formatTime(oldest.occurred_at)} → ${formatTime(latest.occurred_at)}`;
  return (
    <li className="relative pb-5 last:pb-0">
      <span className="absolute -left-[18px] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent/20 ring-2 ring-bg-surface">
        <Sparkles className="h-3 w-3 text-accent" />
      </span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded-md border border-accent/30 bg-accent/5 px-2.5 py-1.5 text-left hover:bg-accent/10 transition-colors"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 text-accent shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-accent shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-mono text-2xs text-text-muted whitespace-nowrap">{timeRange}</span>
            <strong className="text-sm text-text-primary font-medium">{actorLabel(actor)}</strong>
            <span className="text-2xs text-accent px-1.5 py-0.5 rounded bg-accent/15">
              AI 計畫・{entries.length} 個操作
            </span>
          </div>
          <div className="mt-0.5 text-2xs text-text-secondary truncate">{actionSummary}</div>
          {tenantLabel(actor) && showTenant && (
            <div className="mt-0.5 text-2xs text-text-muted">客戶單位：{tenantLabel(actor)}</div>
          )}
        </div>
        <span className="font-mono text-2xs text-text-muted shrink-0" title={`plan_correlation_id: ${planId}`}>
          {planId.slice(0, 8)}…
        </span>
      </button>
      {open && (
        <ol className="relative pl-5 mt-2 ml-2">
          <span className="absolute left-2 top-1 bottom-1 w-px bg-border" aria-hidden="true" />
          {entries.map((e) => (
            <AuditRow key={e.id} entry={e} showTenant={showTenant} />
          ))}
        </ol>
      )}
    </li>
  );
}
